import { useState, useEffect, useRef, useReducer } from 'react';
import { useAudioRecorderGeneral } from '../../../hooks/useAudioRecorderGeneral';
import { handleError } from '../../../context/errors/errorUtils';
import { useAppConfig } from '../../../context/AppConfigContext';
import { toggleTranslation, playUserAudio } from '../../../utils/dialogUtils';
import { useAssistantSingleTranslation } from '../../../hooks/useAssistantFeedback';
import styles from './TranslateBlock.module.css';
import common from '../Common.module.css';
import { Capacitor } from '@capacitor/core';
import waitGif from '../../wait100.gif';

import AudioTimeEnded from '../../../components/AudioTimeEnded/AudioTimeEnded';


// Редьюсер для управления состоянием перевода
const translateReducer = (state, action) => {
  switch (action.type) {
    case 'SET_RECOGNIZED_TEXT':
      return { ...state, recognizedText: action.payload };
    case 'SET_AUDIO_BLOB':
      return { ...state, audioBlob: action.payload };
    case 'SET_CURRENT_SENTENCE':
      return { ...state, currentSentenceIndex: action.payload, recognizedText: '', audioBlob: null, matchFailed: false, matchedOptionIndex: null, correctAnswer: null };
    case 'TOGGLE_TRANSLATION':
      return { ...state, showTranslation: { ...state.showTranslation, [action.payload]: !state.showTranslation[action.payload] } };
    case 'SET_MATCH_FAILED':
      return { ...state, matchFailed: action.payload };
    case 'SET_MATCHED_OPTION_INDEX':
      return { ...state, matchedOptionIndex: action.payload };
    case 'SET_CORRECT_ANSWER':
      return { ...state, correctAnswer: action.payload };
	case 'INCREMENT_PHRASES_COUNT':
	  return { ...state, phrasesCount: state.phrasesCount + 1 };	  
	case 'SET_SERVER_RESPONSE':
	  return { ...state, phrasesCount: 1 };
	case 'SET_AUDIO_TIME_ENDED':
	  return { ...state, audioTimeEnded: action.payload }; 
	case 'RESET_AUDIO_TIME_ENDED':
	  return { ...state, audioTimeEnded: false };	  
    default:
      return state;
  }
};

const TranslateBlock = ({
  userId,
  lessonId,
  block,
  onComplete,
  previousScore = 0,
  onGoPremium = null,
}) => {
  const buttonRef = useRef(null);
  const { isTelegramWebApp, t, baseLanguage, targetLanguage, isAndroidApp, site } = useAppConfig();
  const [delayMs, setDelayMs] = useState(30000);
  const [activeSentences, setActiveSentences] = useState([]);
  const [completedSentences, setCompletedSentences] = useState(0);
  const [score, setScore] = useState(previousScore);
  const [phrases, setPhrases] = useState(0);
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const [isRoundCompleted, setIsRoundCompleted] = useState(false);
  const [manualRecordingStarted, setManualRecordingStarted] = useState(false);
  const [state, dispatch] = useReducer(translateReducer, {
    recognizedText: '',
    audioBlob: null,
    currentSentenceIndex: 0,
	phrasesCount: 1,
    showTranslation: {},
    matchFailed: false,
    matchedOptionIndex: null,
    correctAnswer: null,
	audioTimeEnded: false,
  });
  
  const isNative = Capacitor.isNativePlatform();
  const combinedIsAndroidApp = isAndroidApp || isNative;

  const { currentSentenceIndex, showTranslation, recognizedText, phrasesCount, audioBlob, matchFailed, matchedOptionIndex, correctAnswer, audioTimeEnded } = state;

  // Инициализация ассистента
  useAssistantSingleTranslation({ audioBlob, recognizedText, matchFailed, dispatch, baseLanguage, targetLanguage });

  // Обработка результата распознавания
  useEffect(() => {
    if (!recognizedText) return;

    // Локальное распознавание (speechRecognition)
    if (matchedOptionIndex !== null) {
      dispatch({ type: 'SET_MATCH_FAILED', payload: false });
      dispatch({ type: 'SET_ASSISTANT_SUCCESS', payload: true });
	  dispatch({ type: 'INCREMENT_PHRASES_COUNT' });
      handleNextSentence(true);
    }
    // Серверный ответ
    else if (correctAnswer === true) {
      dispatch({ type: 'SET_MATCH_FAILED', payload: false });
      dispatch({ type: 'SET_ASSISTANT_SUCCESS', payload: true });
      
      handleNextSentence(true);
    }
    // Неверное распознавание
    else if (correctAnswer === false || (matchedOptionIndex === null && correctAnswer === null)) {
      dispatch({ type: 'SET_MATCH_FAILED', payload: true });
      dispatch({ type: 'SET_ASSISTANT_SUCCESS', payload: false });
    }
  }, [recognizedText, matchedOptionIndex, correctAnswer]);

  // Формируем все фразы
  const allSentences = block.words.flatMap((word, wordIndex) =>
    word.examples.map((example, exampleIndex) => {
      const globalIndex = `${wordIndex}-${exampleIndex}`;
      return {
        phrase: example.phrase,
        translation: example.translation,
        audio: example.audio,
        words: example.translation
          .split(' ')
          .map((word, wordIndexInTranslation) => ({
            id: `${globalIndex}-${wordIndexInTranslation}-${word}`,
            value: word,
          })),
        index: globalIndex,
      };
    })
  );

  // Перемешиваем фразы
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Инициализация активных фраз
  useEffect(() => {
    if (activeSentences.length === 0) {
      const shuffledSentences = shuffleArray(allSentences);
      setActiveSentences(shuffledSentences.slice(0, Math.min(10, shuffledSentences.length)));
      setCompletedSentences(0);
      setIsRoundCompleted(false);
      dispatch({ type: 'SET_CURRENT_SENTENCE', payload: 0 });
    }
  }, [activeSentences.length, allSentences]);

  const currentSentence = activeSentences[currentSentenceIndex];

  // Объект шага с ожидаемой фразой в options
  const step = {
    id: currentSentence ? `${block.block_id}-${currentSentence.index}` : `${block.block_id}-default`,
    lessonId,
    options: currentSentence ? [{ userText: currentSentence.translation }] : [],
	phrasesCount
  };
  
  // Обработка следующей фразы
  const handleNextSentence = (isCorrect) => {
    const newScore = isCorrect ? score + 1 : score;
    setScore(newScore);
    setPhrases((prev) => prev + 1); // зачисляем фразы, потом возвращаем для анимации
    setCompletedSentences((prev) => prev + 1);
  
    if (isCorrect && currentSentence?.audio) {
      setShowCorrectAnswer(true);
	  const audioSrc = `${combinedIsAndroidApp ? site : ''}${currentSentence.audio}`;
      playUserAudio(
        audioSrc,
        dispatch,
        () => setTimeout(goToNextSentence, 2500), // onEnded
        () => setTimeout(goToNextSentence, 2500)  // onError
      );
    } else {
      goToNextSentence();
    }
  };
  
  const goToNextSentence = () => {
    const nextSentenceIndex = currentSentenceIndex + 1;
    setShowCorrectAnswer(false);
    setManualRecordingStarted(false);
    if (nextSentenceIndex < activeSentences.length) {
      dispatch({ type: 'SET_CURRENT_SENTENCE', payload: nextSentenceIndex });
    } else {
      setIsRoundCompleted(true);
    }
  };

  // Инициализация хука записи
  const recorder = useAudioRecorderGeneral({ 
    userId,
    stepId: step.id,
    contextId: block.block_id,
    source: 'lesson',
    dispatch, 
    step,
    state,
    buttonRef,
    isTelegramWebApp,
  });

  useEffect(() => {
    if (!recorder.isRecording && !recorder.isProcessing) {
      setManualRecordingStarted(false);
    }
  }, [recorder.isRecording, recorder.isProcessing]);

  // Задержка между фразами
  useEffect(() => {
    if (isRoundCompleted) return;
    if (activeSentences.length > 0 && !audioBlob && !recorder.isRecording && !recorder.isProcessing && !showCorrectAnswer) {
      const timer = setTimeout(() => {
        handleNextSentence(false);
      }, delayMs);
      return () => clearTimeout(timer);
    }
  }, [
    currentSentenceIndex,
    activeSentences,
    delayMs,
    audioBlob,
    recorder.isRecording,
    recorder.isProcessing,
    showCorrectAnswer,
    isRoundCompleted,
  ]);

  // Проверка обязательных данных
  useEffect(() => {
    if (!block.block_id) {
      handleError({
        code: 'MISSING_IDS',
        error: new Error(t.translateBlock.missing_ids),
        context: { module: 'TranslateBlock', block_id: block.block_id },
        showToUser: true,
      });
    }
  }, [block.block_id, t]);

  if (audioTimeEnded) { return <AudioTimeEnded onBack={() => { 
		dispatch({ type: 'RESET_AUDIO_TIME_ENDED' });
		onComplete(score, phrases);
	}} onGoPremium={onGoPremium} />; }  

  
  if (!recorder.isSupported) {
    return <div className={styles.error}>{t.translateBlock.audio_not_supported}</div>;
  }
  
	return (
	  <div className={styles.translateScreen}>
	  
      {isRoundCompleted ? (
        <div className={common.completed}>
          <p>{t.sentenceBlock.all_phrases_are_complete}</p>
          <button className={common.nextButton} onClick={() => onComplete(score, phrases)}>
            {t.sentenceBlock.next}
          </button>
        </div>
      ) : (
		  // Всё остальное отображается, пока не завершили
		  <>
			<h1 className={styles.translateTitle}>
			  {t.translateBlock.title.replace('{blockTitle}', block.blockTitle)} ({completedSentences + 1}/{activeSentences.length})
			</h1>

			<div className={styles.introBlock}>
			  <div className={styles.introTextContainer} onClick={() => toggleTranslation(dispatch, `phrase-${step.id}`)} >
				{currentSentence && (
				  <>
					<p>{currentSentence.phrase}</p>
					{showCorrectAnswer ? (
					  <p className={styles.correctAnswer}>{currentSentence.translation}</p>
					) : showTranslation[`phrase-${step.id}`] ? (
					  <p className={styles.translation}>{currentSentence.translation}</p>
					) : null}
				  </>
				)}
			  </div>
			</div>
			
            {manualRecordingStarted && recorder.isProcessing && (
              <div className={styles.loading}>
                <img src={waitGif} alt="Loading..." />
              </div>
            )}

			{!audioBlob && !showCorrectAnswer && (
			  <div className={styles.recorderContainer}>
				<div className={styles.assistantLine}>
				  <button
					ref={buttonRef}
					type="button"
					className={`${styles.recordButton} ${recorder.isRecording ? styles.recording : ''}`}
					disabled={!recorder.permissionGranted || recorder.isProcessing}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const started = recorder.handleStartRecording(step?.options || []);
                      if (started) setManualRecordingStarted(true);
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      const started = recorder.handleStartRecording(step?.options || []);
                      if (started) setManualRecordingStarted(true);
                    }}
                    onMouseUp={(e) => {
                      e.preventDefault();
                      recorder.handleStopRecording();
                    }}
                    onMouseLeave={(e) => {
                      e.preventDefault();
                      recorder.handleStopRecording();
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      recorder.handleStopRecording();
                    }}
                    onTouchCancel={(e) => {
                      e.preventDefault();
                      recorder.handleStopRecording();
                    }}
				  >
					<svg viewBox="0 0 24 24">
					  <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 14 0h-2zM11 21h2v-2h-2v2z"/>
					</svg>
				  </button>
				</div>
			  </div>
			)}

			<div className={styles.sliderContainer}>
			  <label htmlFor="delayRange" className={styles.sliderLabel}>
				{t.translateBlock.delay_label}: {Math.round(delayMs / 1000)} {t.translateBlock.seconds}
			  </label>
			  <input
				id="delayRange"
				type="range"
				min="7000"
				max="30000"
				step="500"
				value={delayMs}
				onChange={(e) => setDelayMs(Number(e.target.value))}
				className={styles.slider}
			  />
			</div>
		  </>
		)}
	  </div>
	);
};
export default TranslateBlock;
